import React, { FunctionComponent, useContext } from 'react';
import DataRecordTable, { CellTypeEnum } from 'src/libraries/data-record-table';
import Translate from 'ui/atoms/translate';
import { AdminApi, AdminProductPrice, AdminProductPricesListOrderingEnum } from 'api';
import WithDataRecord from 'src/hoc/WithDataRecord';
import { PaginationProps } from 'ui/molecules/pagination';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Button from 'src/ui/atoms/button';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import { I18NContext } from 'src/ui/i18n/provider';

import Currency from 'src/ui/atoms/currency';
import Date from 'src/ui/atoms/date';
import { Search } from 'src/ui/molecules/filtering';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';

export interface MarketPricesTableProps {
  loading?: boolean;
  paginationProps?: PaginationProps;
  defaultLimit?: number;
  forceDataLoad: boolean;
  setForceDataLoad: (val: boolean) => void;
  skipFilterUrlSync?: boolean;
  productId: string;
  hasManageMarketPermission: boolean;
  onDelete: (marketPrice: AdminProductPrice) => void;
}

const MarketPricesTable: FunctionComponent<MarketPricesTableProps> = WithDataRecord<AdminApi, AdminProductPrice>(
  AdminApi,
)(
  ({ data, loading, paginationProps, onOrderBy, ordering, hasManageMarketPermission, onDelete }) => {
    const { activeLocale } = useContext(I18NContext);
    const translate = useTranslateWithStringArgs();

    return (
      <DataRecordTable
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={data as AdminProductPrice[]}
        loading={loading}
        paginationProps={paginationProps}
        searchComponent={<Search input="id" defaultField="id" label={translate('filters.labels.priceId')} name="id" />}
        cells={[
          {
            title: <Translate name="marketPrices.marketPrice" />,
            render: (marketPrice) => <Currency>{marketPrice.price}</Currency>,
            orderingFieldName: AdminProductPricesListOrderingEnum.PRICE_ASC,
            type: CellTypeEnum.currency,
            filterName: 'price',
          },
          {
            title: <Translate name="marketPrices.dateAndTime" />,
            render: (marketPrice) => <Date showTime>{marketPrice.priceAt}</Date>,
            orderingFieldName: AdminProductPricesListOrderingEnum.PRICE_AT_ASC,
            type: CellTypeEnum.date,
            filterName: 'priceAt',
          },
          {
            title: <Translate name="marketPrices.actions" />,
            render: (marketPrice) => (
              <ActionButtons align="center" spacingTop="none">
                {!hasManageMarketPermission ? (
                  <Tooltip
                    content={<Translate name="marketPrices.noDeletePermission" />}
                    position={TooltipPosition.LEFT}
                  >
                    <Button variant="secondary" size="xsmall" disabled={true}>
                      <Translate name="common.delete" />
                    </Button>
                  </Tooltip>
                ) : (
                  <Button variant="secondary" onClick={() => onDelete(marketPrice)} size="xsmall">
                    <Translate name="common.delete" />
                  </Button>
                )}
              </ActionButtons>
            ),
            alignContent: 'left',
            width: activeLocale === 'de' ? 112 : 100,
          },
        ]}
      />
    );
  },
  (api, props, offset, limit) => {
    return api.adminProductPricesList({
      offset,
      limit,
      ...props,
      productId: [props.productId],
    });
  },
);

export default MarketPricesTable;
